<template>
  <el-col :span="props.spanColumn">
    <div class="label">{{ props.label }}</div>
    <el-row style="margin-bottom: 0">
      <el-select
        v-model="selectedEjecutivo"
        :size="props.settings.size"
        :disabled="props.settings.disabled"
        clearable
        filterable
        remote
        :remote-method="fetchUsers"
        :loading="isLoading"
        :placeholder="props.settings.placeholder"
        @change="emitChange"
        @clear="emitClear"
      >
        <el-option
          v-for="user in options"
          :key="user.Codigo"
          :label="user.Nombre + ' ' + user.Apellido"
          :value="user.Codigo"
        />
      </el-select>
    </el-row>
  </el-col>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
  onMounted,
} from 'vue';
import { useEjecutivoStore } from '@/stores/ejecutivo_store';
import { storeToRefs } from 'pinia';

const props = defineProps({
  spanColumn: Number,
  label: String,
  settings: {
    placeholder: String,
    size: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    isProspect: {
      type: Boolean,
      default: false,
    },
  },
  modelValue: {
    type: [String, Number],
  },
  prefillUser: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'change']);

const selectedEjecutivo = ref(props.modelValue);
const options = ref([]);
const isLoading = ref(false);

watch(
  () => props.modelValue,
  async (val) => {
    selectedEjecutivo.value = val;
    await ensureSelectedInOptions();
  },
);

const ejecutivoStore = useEjecutivoStore();
const { searchEjecutivosHTTP, ensureEjecutivoInCache } = ejecutivoStore;

function mergeUniqueByCodigo(base = [], extra = []) {
  const map = new Map(base.map((u) => [u.Codigo, u]));
  for (const u of extra) if (u?.Codigo && !map.has(u.Codigo)) map.set(u.Codigo, u);
  return Array.from(map.values());
}

async function ensureSelectedInOptions() {
  const codigo = Number(selectedEjecutivo.value);
  if (!codigo) return;
  if (options.value.some((u) => u.Codigo === codigo)) return;

  if (props.prefillUser?.Codigo === codigo) {
    options.value = mergeUniqueByCodigo([props.prefillUser], options.value);
    return;
  }

  const user = await ensureEjecutivoInCache(codigo, props.settings.isProspect ? 1 : undefined);
  if (user) {
    options.value = mergeUniqueByCodigo([user], options.value);
  }
}

const emitClear = () => {
  selectedEjecutivo.value = null;
  emit('update:modelValue', null);
  emit('change', null);
};

const fetchUsers = async (query) => {
  isLoading.value = true;
  try {
    const q = (query ?? '').toString().trim();
    if (q === '' || q.length >= (props.settings.minChars ?? 3)) {
      const results = await searchEjecutivosHTTP(q, props.settings.isProspect ? 1 : undefined);
      options.value = mergeUniqueByCodigo(
        options.value.filter((u) => u.Codigo === Number(selectedEjecutivo.value)),
        results || [],
      );
    } else {
      await ensureSelectedInOptions();
    }
  } finally {
    isLoading.value = false;
  }
};

const emitChange = () => {
  emit('update:modelValue', selectedEjecutivo.value);
  emit('change', selectedEjecutivo.value);
};

onMounted(async () => {
  await ensureSelectedInOptions();
  await fetchUsers('');
});
</script>

<style scoped>
.label {
  font-weight: 700;
  color: #111;
}
</style>
